import PlayArrow from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { TikTokEmbed } from '@/components/media';
import { TikTokIcon } from '@/components/molecules';
import { type DeliveryGalleryItem } from '@/services/deliveries';
import { deliveryMeta } from '@/utils/deliveryFormat';

type Props = {
  item: DeliveryGalleryItem;
  /** The grid or the strip decides the tile's shape; the tile never does — matches DeliveryTile. */
  sx?: SxProps<Theme>;
};

/**
 * A delivery's TikTok link, as its own tile.
 *
 * Deliberately NOT `DeliveryTile`: that one's `onClick` opens the lightbox or
 * the reel viewer, and TikTok's embed fits neither — it has no play/API
 * control to drive from a "next" swipe, and a fixed vertical shape with
 * TikTok's own chrome baked in, not a file this site can crop or letterbox.
 * So a tap loads the player in place instead of opening anything.
 *
 * Before that tap, nothing here has reached tiktok.com — `TikTokEmbed`, and
 * the network request inside it, only mounts once `loaded` is true.
 */
export function DeliveryTikTokTile({ item, sx }: Props) {
  const [loaded, setLoaded] = useState(false);
  const { delivery, media } = item;

  return (
    <Box
      sx={[
        (theme) => ({
          position: 'relative',
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'common.black',
          boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {loaded ? (
        <TikTokEmbed url={media.src} />
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => setLoaded(true)}
          aria-label={`Play TikTok video: ${delivery.vehicle}, ${deliveryMeta(delivery)}`}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            height: '100%',
            p: 0,
            border: 0,
            cursor: 'pointer',
            color: 'common.white',
            // A staff pick, not TikTok's own — see toTikTokMedia for why.
            // Falls back to the flat gradient until someone picks one.
            background: media.poster
              ? `linear-gradient(160deg, rgba(0,0,0,0.32), rgba(0,0,0,0.52)), center / cover no-repeat url("${media.poster}")`
              : 'linear-gradient(160deg, #262626 0%, #050505 100%)',
            backgroundColor: '#050505',
            '&:hover .delivery-tiktok-play': { transform: 'scale(1.06)' },
            '&:focus-visible': {
              outline: '3px solid',
              outlineColor: 'primary.main',
              outlineOffset: -3,
            },
          }}
        >
          <TikTokIcon sx={{ fontSize: 36 }} aria-hidden />
          <Stack
            className="delivery-tiktok-play"
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.14)',
              borderRadius: 999,
              px: 2.5,
              py: 1,
              transition: 'transform 220ms ease',
            }}
          >
            <PlayArrow sx={{ fontSize: 18 }} aria-hidden />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Watch on TikTok
            </Typography>
          </Stack>

          {/* Aria-hidden: every word is already in this button's name. */}
          <Stack
            aria-hidden
            spacing={0.5}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: 3,
              pt: 10,
              pb: 3,
              textAlign: 'left',
              background:
                'linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 52%, rgba(0,0,0,0) 100%)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {delivery.vehicle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.82)' }}>
              {deliveryMeta(delivery)}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
